import React from 'react';
import { Link } from 'react-router-dom'; // looks like this was removed from the overview tab - non-overview tab could follow suit?
import {
  ExpansionPanel,
  ExpansionPanelActionEvent,
  ExpansionPanelContent,
} from '@progress/kendo-react-layout';
import { Reveal } from '@progress/kendo-react-animation';
import {
  TabCardContainer,
  CardItemTitleMobile,
  CardItemTitleDesktop,
  CardItemTitleNonOverview,
  HelpSection,
  TabCardContainerPayments,
  HelpIcon,
  NoPaymentsContainer,
} from '../../components/tw-components';
import { HighlightNote, baseShadowSmall } from '../../../../base/tw-components';
import './index.css';
import { isEmptyObject } from '../payments';
import { ICaseHistoryEvent } from '../../../../../data-models';
import { useCaseHistory } from '../../../../../hooks/use-data';
import { Spinner } from '../../../../../components/base/spinner';

export type SectionMappingProps = {
  sectionMapping: {
    [key: string]: {
      title: string;
      headerLinkName: string;
      headerLink: string;
      component: React.ReactNode;
      popupContent: string;
    };
  };
};

export type TabCardsNonOverviewProps = SectionMappingProps & {
  isDetailsTab?: boolean;
  hasLetters?: boolean;
  hasTAbsId?: boolean;
  ntn?: string;
};

export type OpenSectionState = string | null | string[];

type ReturnComponentProps = SectionMappingProps & {
  openSection: OpenSectionState;
  setOpenSection: React.Dispatch<React.SetStateAction<OpenSectionState>>;
  ntn?: string;
};

export const TabCards = ({
  sectionMapping,
  openSection,
  setOpenSection,
  ntn,
}: ReturnComponentProps) => {
  const entries = Object.entries(sectionMapping);
  const [isActivityHistoryOpen, setIsActivityHistoryOpen] =
    React.useState(false);
  const leftColumnRef = React.useRef<HTMLDivElement | null>(null);
  const [leftColumnHeight, setLeftColumnHeight] = React.useState<number>(0);

  const hasNtn = !!ntn;

  const {
    data: caseHistory = [],
    isFetching: isFetchingCaseHistory,
    isError: isErrorCaseHistory,
    error: caseHistoryError,
  } = useCaseHistory(hasNtn ? ntn : undefined) as {
    data: ICaseHistoryEvent[] | undefined;
    isFetching: boolean;
    isError: boolean;
    error: unknown;
  };

  const eventKeyOccurrences = new Map<string, number>();
  const visibleCaseHistory = caseHistory
    .filter(
      (event) =>
        event.eventType !== 'Archived' && event.eventType !== 'Escalated',
    )
    .map((event) => {
      const identity = JSON.stringify([event.eventId, event.occurredAt]);
      const occurrence = eventKeyOccurrences.get(identity) ?? 0;
      eventKeyOccurrences.set(identity, occurrence + 1);
      return { event, key: JSON.stringify([identity, occurrence]) };
    });

  const showError = isErrorCaseHistory;

  const errorMessage =
    caseHistoryError instanceof Error
      ? caseHistoryError.message
      : 'An error occurred while fetching the activity history.';

  const isSectionOpen = (section: string) =>
    Array.isArray(openSection)
      ? openSection.includes(section)
      : openSection === section;

  const handleSectionAction =
    (section: string) => (event: ExpansionPanelActionEvent) => {
      if (Array.isArray(openSection)) {
        if (event.expanded) {
          const next = openSection.filter((s) => s !== section);
          setOpenSection(next.length > 0 ? next : null);
        } else {
          setOpenSection([...openSection, section]);
        }
        return;
      }

      setOpenSection(event.expanded ? null : section);
    };

  const panelActionLabel = (isOpen: boolean) => (isOpen ? 'Minimize' : 'View');

  const formatEventDate = (dateString: string): string => {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${formattedDate} at ${formattedTime}`;
  };

  const renderEventType = (event: ICaseHistoryEvent): string => {
    let eventType = event?.eventType;
    if (event.category === 'CASE_STATUS') {
      eventType = event.category.replace('_', ' ');
    } else if (
      event.eventType.includes('Received') ||
      event.eventType.includes('Completed')
    ) {
      eventType = event.summary ?? '';
    }

    const normalizedEventType = eventType.toLowerCase();

    return `${normalizedEventType
      .charAt(0)
      .toUpperCase()}${normalizedEventType.slice(1)}`;
  };

  const renderEventSummary = (event: ICaseHistoryEvent): React.ReactNode => {
    const details = event.details ?? {};

    switch (event.category) {
      case 'PAYMENT':
        return `Payment method: ${event.summary ?? ''}`.trim();
      case 'CASE_STATUS':
        return event.eventType;
      case 'TASK':
        if (
          (event.eventType === 'Task Created' ||
            event.eventType === 'Tasks Created') &&
          Array.isArray(details.tasks)
        ) {
          const taskTitles = details.tasks
            .map((task: { taskTitle?: string }) => task.taskTitle)
            .filter((taskTitle: string | undefined): taskTitle is string =>
              Boolean(taskTitle),
            );

          if (taskTitles.length === 0) {
            return event.summary ?? '';
          }

          if (taskTitles.length === 1) {
            return taskTitles[0];
          }

          return (
            <ul className="tw-list-disc tw-pl-5 tw-m-0">
              {taskTitles.map((taskTitle: string, index: number) => (
                <li key={`${taskTitle}-${index}`}>{taskTitle}</li>
              ))}
            </ul>
          );
        } else if (
          event.eventType.includes('Received') ||
          event.eventType.includes('Completed')
        ) {
          return event.eventType;
        }
        return event.summary ?? '';
      case 'DOCUMENT':
        return (
          <>
            {details.docType && <div>{details.docType}</div>}
            {details.fileName && <div>{details.fileName}</div>}
          </>
        );
      case 'CONTACT': {
        return details.direction === 'Contacted from EE'
          ? 'Contact from Employee'
          : 'Contacted by Unum';
      }
      default:
        return event.summary ?? '';
    }
  };

  React.useEffect(() => {
    const target = leftColumnRef.current;
    if (target) {
      let frameId: number | null = null;

      const updateHeight = () => {
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
        frameId = requestAnimationFrame(() => {
          setLeftColumnHeight(target.offsetHeight);
        });
      };

      updateHeight();

      const onWindowResize = () => {
        updateHeight();
      };
      window.addEventListener('resize', onWindowResize);

      const resizeObserver =
        typeof ResizeObserver === 'function'
          ? new ResizeObserver(() => {
              updateHeight();
            })
          : null;

      resizeObserver?.observe(target);

      const mutationObserver =
        typeof MutationObserver === 'function'
          ? new MutationObserver(() => {
              updateHeight();
            })
          : null;

      mutationObserver?.observe(target, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
      });

      return () => {
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
        window.removeEventListener('resize', onWindowResize);
        resizeObserver?.disconnect();
        mutationObserver?.disconnect();
      };
    }
  }, [entries.length, isActivityHistoryOpen]);

  return (
    <>
      {/*----------------------------------- Mobile View -----------------------------------*/}
      <div className="tw-block md:tw-hidden">
        <div className="tw-grid tw-gap-4 tw-px-0 sm:tw-px-3">
          {entries.map(
            ([
              section,
              { title, headerLinkName, headerLink, component, popupContent },
            ]) => (
              <TabCardContainer key={section}>
                <ExpansionPanel
                  title={
                    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-3">
                      <CardItemTitleMobile>
                        <HelpSection
                          title={title}
                          popupContent={popupContent}
                          headerLinkName={headerLinkName}
                          headerLink={headerLink}
                          showDesktopLink={false}
                        />
                      </CardItemTitleMobile>
                      <span className="tw-text-md tw-font-normal tw-text-unm-interactive01-400 tw-whitespace-nowrap">
                        {panelActionLabel(isSectionOpen(section))}
                      </span>
                    </div>
                  }
                  expanded={isSectionOpen(section)}
                  onAction={handleSectionAction(section)}
                  className={`tw-border tw-rounded-lg ${baseShadowSmall}`}
                >
                  <Reveal>
                    {isSectionOpen(section) && (
                      <ExpansionPanelContent>{component}</ExpansionPanelContent>
                    )}
                  </Reveal>
                </ExpansionPanel>
              </TabCardContainer>
            ),
          )}

          <TabCardContainer>
            <ExpansionPanel
              title={
                <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-3">
                  <CardItemTitleMobile>
                    <span>Activity History</span>
                  </CardItemTitleMobile>
                  <span className="tw-text-md tw-font-normal tw-text-unm-interactive01-400 tw-whitespace-nowrap">
                    {panelActionLabel(isActivityHistoryOpen)}
                  </span>
                </div>
              }
              expanded={isActivityHistoryOpen}
              onAction={() => {
                setIsActivityHistoryOpen((previousValue) => !previousValue);
              }}
              className={`tw-border tw-rounded-lg ${baseShadowSmall}`}
            >
              <Reveal>
                {isActivityHistoryOpen && (
                  <ExpansionPanelContent>
                    <div className="tw-p-4 tw-bg-white tw-text-sm tw-text-unm-adhoc2">
                      {isFetchingCaseHistory && (
                        <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-pb-4">
                          <Spinner></Spinner>
                        </div>
                      )}

                      {showError && (
                        <div className="tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-text-red-700">
                          <p className="tw-font-semibold tw-mb-1">
                            Unable to load activity history
                          </p>
                          <p className="tw-text-sm">{errorMessage}</p>
                        </div>
                      )}

                      {/* Success State - Empty */}
                      {!isFetchingCaseHistory &&
                        !showError &&
                        visibleCaseHistory.length === 0 && (
                          <div className="tw-p-3 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded tw-text-gray-600">
                            <p>No activity history available.</p>
                          </div>
                        )}

                      {/* Success State - Data */}
                      {!isFetchingCaseHistory &&
                        !showError &&
                        visibleCaseHistory.length > 0 && (
                          <div className="tw-space-y-0">
                            {visibleCaseHistory.map(({ event, key }, idx) => {
                              const isLast =
                                idx === visibleCaseHistory.length - 1;
                              return (
                                <div key={key} className="tw-flex tw-gap-4">
                                  {/* Timeline dot and line */}
                                  <div className="tw-flex tw-flex-col tw-items-center tw-flex-shrink-0 tw-w-3">
                                    {/* Dot */}
                                    <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-unm-interactive01-400 tw-flex-shrink-0 tw-mt-1" />
                                    {/* Connecting line */}
                                    {!isLast && (
                                      <div className="tw-w-0.5 tw-flex-grow tw-bg-gray-300 tw-my-1" />
                                    )}
                                  </div>
                                  {/* Event content */}
                                  <div className="tw-flex-1 tw-min-w-0 tw-mb-2 tw-text-unm-adhoc2">
                                    <p className="tw-text-base tw-font-semibold tw-leading-6 tw-mb-1">
                                      {renderEventType(event)}
                                    </p>
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-my-1">
                                      {/* eventSummary */}
                                      <div className="tw-text-sm tw-font-medium tw-leading-5">
                                        {renderEventSummary(event)}
                                      </div>
                                      <span className="tw-text-sm tw-leading-5">
                                        {formatEventDate(event.occurredAt)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  </ExpansionPanelContent>
                )}
              </Reveal>
            </ExpansionPanel>
          </TabCardContainer>
        </div>
      </div>

      {/*----------------------------------- Desktop View -----------------------------------*/}
      <div className="tw-hidden md:tw-grid md:tw-grid-cols-[minmax(0,55fr)_minmax(0,45fr)] md:tw-gap-4 md:tw-px-0 md:tw-w-full md:tw-items-start md:tw-max-w-full">
        <div
          ref={leftColumnRef}
          className="tw-flex tw-flex-col tw-gap-4 tw-min-w-0"
        >
          {entries.map(
            ([
              section,
              { title, headerLinkName, headerLink, component, popupContent },
            ]) => (
              <ExpansionPanel
                key={section}
                title={
                  <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-3">
                    <CardItemTitleDesktop>
                      <HelpSection
                        title={title}
                        popupContent={popupContent}
                        headerLinkName={headerLinkName}
                        headerLink={headerLink}
                        showDesktopLink={false}
                      />
                    </CardItemTitleDesktop>
                    <span className="tw-text-md tw-font-normal tw-text-unm-interactive01-400 tw-whitespace-nowrap">
                      {panelActionLabel(isSectionOpen(section))}
                    </span>
                  </div>
                }
                expanded={isSectionOpen(section)}
                onAction={handleSectionAction(section)}
                className={`tw-border tw-rounded-lg ${baseShadowSmall}`}
              >
                <Reveal>
                  {isSectionOpen(section) && (
                    <ExpansionPanelContent>{component}</ExpansionPanelContent>
                  )}
                </Reveal>
              </ExpansionPanel>
            ),
          )}
        </div>

        <div
          className="tw-flex tw-min-w-0 tw-w-full"
          style={
            leftColumnHeight > 0
              ? { height: `${leftColumnHeight}px` }
              : undefined
          }
        >
          <TabCardContainer className="tw-w-full tw-h-full tw-min-w-0 tw-box-border">
            <div
              className={`tw-h-full tw-p-4 tw-pr-1 lg:tw-p-6 lg:tw-pr-1 tw-bg-white tw-flex tw-flex-col tw-overflow-hidden 
                            tw-box-border tw-border tw-rounded-lg ${baseShadowSmall}`}
            >
              <CardItemTitleDesktop>
                <span>Activity History</span>
              </CardItemTitleDesktop>
              <div className="tw-text-sm tw-text-unm-adhoc2 tw-flex-1 tw-min-w-0 tw-box-border tw-overflow-y-auto tw-mt-4">
                {/* Loading State */}
                {isFetchingCaseHistory && (
                  <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-pb-4">
                    <Spinner></Spinner>
                  </div>
                )}

                {/* Error State */}
                {showError && (
                  <div className="tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-text-unm-error-red">
                    <p className="tw-font-semibold tw-mb-1">
                      Unable to load activity history
                    </p>
                    <p className="tw-text-sm">{errorMessage}</p>
                  </div>
                )}

                {/* Success State - Empty */}
                {!isFetchingCaseHistory &&
                  !showError &&
                  visibleCaseHistory.length === 0 && (
                    <div className="tw-p-3 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded tw-text-gray-600">
                      <p>No activity history available.</p>
                    </div>
                  )}

                {/* Success State - Data */}
                {!isFetchingCaseHistory &&
                  !showError &&
                  visibleCaseHistory.length > 0 && (
                    <div className="tw-space-y-0">
                      {visibleCaseHistory.map(({ event, key }, idx) => {
                        const isLast = idx === visibleCaseHistory.length - 1;
                        return (
                          <div key={key} className="tw-flex tw-gap-4">
                            {/* Timeline dot and line */}
                            <div className="tw-flex tw-flex-col tw-items-center tw-flex-shrink-0 tw-w-3">
                              {/* Dot */}
                              <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-unm-interactive01-400 tw-flex-shrink-0 tw-mt-1" />
                              {/* Connecting line */}
                              {!isLast && (
                                <div className="tw-w-0.5 tw-flex-grow tw-bg-gray-300 tw-my-1" />
                              )}
                            </div>
                            {/* Event content */}
                            <div className="tw-flex-1 tw-min-w-0 tw-mb-2 tw-text-unm-adhoc2">
                              <p className="tw-text-base tw-font-semibold tw-leading-6 tw-mb-1">
                                {renderEventType(event)}
                              </p>
                              <div className="tw-text-sm tw-font-sm tw-leading-6 tw-mb-0">
                                {renderEventSummary(event)}
                              </div>
                              <p className="tw-text-sm tw-my-1 tw-leading-5">
                                {formatEventDate(event.occurredAt)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            </div>
          </TabCardContainer>
        </div>
      </div>
    </>
  );
};

export const TabCardsNonOverview = ({
  sectionMapping,
  isDetailsTab,
  hasLetters,
  hasTAbsId,
  ntn,
}: TabCardsNonOverviewProps) => {
  return (
    <div className=" tw-gap-2" data-ntn={ntn || undefined}>
      {isDetailsTab && (hasLetters || hasTAbsId) && (
        <div className="tw-mb-3 tw-mx-3 md:tw-mx-0">
          <HighlightNote>
            Below are the plans Unum has evaluated for your case. Your requested
            time is reviewed for all plans you may be eligible for.
          </HighlightNote>
        </div>
      )}

      {!isEmptyObject(sectionMapping)
        ? Object.entries(sectionMapping).map(
            ([
              section,
              { title, headerLinkName, headerLink, component, popupContent },
            ]) => (
              <TabCardContainerPayments key={section}>
                <div className="tw-rounded tw-pt-2 tw-px-3 tw-bg-[#FCFCFC]">
                  <CardItemTitleNonOverview>
                    <div className="tw-flex tw-justify-start tw-items-center">
                      <p className="tw-inline-block tw-m-0 tw-mr-2">{title}</p>
                      <HelpIcon popupContent={popupContent}></HelpIcon>
                    </div>
                    <Link
                      to={headerLink}
                      className="tw-no-underline tw-leading-8 tw-text-sm tw-align-middle"
                    >
                      <div>{headerLinkName}</div>
                    </Link>
                  </CardItemTitleNonOverview>
                  {component}
                </div>
              </TabCardContainerPayments>
            ),
          )
        : !isDetailsTab && <NoPaymentsContainer></NoPaymentsContainer>}
    </div>
  );
};
